/**
 * Google Maps marker icons must be self-contained: external images inside an
 * SVG data URL are not reliably loaded by the marker renderer. Embed the same
 * size-tiered Lift resource used by SportIcon, keeping only the pin shell themed.
 */
import type { SportName } from '@bravemove/design-tokens/icons';
import { getSportArtworkUrl } from '@bravemove/design-tokens/icons/artwork-web';

export type ComposeVenuePinArgs = {
  readonly sportKey: SportName | string;
  readonly tier: 'tier_1' | 'tier_2' | 'tier_3';
  readonly accentHex: string;
  readonly inkHex: string;
  /** Selected pins are larger with a stronger outline; artwork stays unchanged. */
  readonly selected?: boolean;
};

const artworkCache = new Map<string, Promise<string>>();
const pinCache = new Map<string, Promise<string>>();

function embeddedArtwork(url: string): Promise<string> {
  const cached = artworkCache.get(url);
  if (cached) return cached;

  const pending = fetch(url)
    .then(async (response) => {
      if (!response.ok) throw new Error('Sport artwork could not be loaded');
      const blob = await response.blob();
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') resolve(reader.result);
          else reject(new Error('Sport artwork could not be encoded'));
        };
        reader.onerror = () => reject(new Error('Sport artwork could not be encoded'));
        reader.readAsDataURL(blob);
      });
    })
    .catch((error: unknown) => {
      // A transient network failure must not poison the cache for later pans.
      artworkCache.delete(url);
      throw error;
    });
  artworkCache.set(url, pending);
  return pending;
}

function pinDataUrl(
  { tier, accentHex, inkHex, selected = false }: ComposeVenuePinArgs,
  artwork?: string,
): string {
  const tierRing =
    tier === 'tier_3'
      ? `<circle cx="20" cy="17" r="13.4" fill="none" stroke="${accentHex}" stroke-width="1.6" opacity="0.5"/>`
      : '';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${selected ? 48 : 40}" height="${selected ? 60 : 50}" viewBox="0 0 40 50">` +
    `<ellipse cx="20" cy="47" rx="5.5" ry="2" fill="${inkHex}" opacity="0.22"/>` +
    `<path d="M20 2C11.7 2 5 8.7 5 17c0 10 15 27 15 27s15-17 15-27C35 8.7 28.3 2 20 2Z" ` +
    `fill="${accentHex}" stroke="${inkHex}" stroke-opacity="${selected ? 1 : 0.18}" stroke-width="${selected ? 2.5 : 1}"/>` +
    `<circle cx="20" cy="17" r="11.5" fill="white"/>` +
    tierRing +
    (artwork
      ? `<image href="${artwork}" x="7" y="4" width="26" height="26" preserveAspectRatio="xMidYMid meet"/>`
      : '') +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Cached, self-contained Lift pin; unknown sport codes use the Lift football. */
export function composeVenuePinSvg(args: ComposeVenuePinArgs): Promise<string> {
  const artworkUrl = getSportArtworkUrl(args.sportKey, 26) ?? getSportArtworkUrl('football-5', 26);
  if (!artworkUrl) return Promise.resolve(pinDataUrl(args));

  const key = JSON.stringify([
    artworkUrl,
    args.tier,
    args.accentHex,
    args.inkHex,
    args.selected ?? false,
  ]);
  const cached = pinCache.get(key);
  if (cached) return cached;

  const pending = embeddedArtwork(artworkUrl)
    .then((artwork) => pinDataUrl(args, artwork))
    .catch(() => {
      pinCache.delete(key);
      // Keep the venue discoverable during a failed request, without substituting
      // an old sport drawing. A later marker redraw retries the canonical asset.
      return pinDataUrl(args);
    });
  pinCache.set(key, pending);
  return pending;
}
